import React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'

import TooltipPortal from 'modules/core/components/DraggableNode'

import FlexBetweenRow from './FlexBetweenRow'

interface Row {
  left: string
  right?: string | number
}

interface HoverableTooltipRecommendationProps {
  label: string
  labelDotted?: boolean
  title: string
  count: number
  emptyMessage?: string
  rows?: Row[]
  details?: React.ReactNode | string
  showCount?: boolean
}

function getDisplayLabel(label: string, count: number, showCount = true) {
  return showCount && count > 0 ? `${count} ${label}` : label
}

const HoverableTooltipRecommendation: React.FC<HoverableTooltipRecommendationProps> = ({
  label,
  labelDotted = true,
  title,
  count,
  emptyMessage,
  rows = [],
  details,
  showCount = true,
}) => {
  const [open, setOpen] = React.useState(false)
  const triggerRef = React.useRef<HTMLSpanElement>(null)
  const [coords, setCoords] = React.useState<{ x: number; y: number } | null>(null)

  const handleMouseEnter = () => {
    setOpen(true)
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setCoords({
        x: rect.left + rect.width / 2,
        y: rect.top,
      })
    }
  }

  const handleMouseLeave = () => {
    setOpen(false)
  }

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: 'pointer' }}
      >
        <Text.Normal dotted={labelDotted} color="black" size="200">
          {getDisplayLabel(label, count, showCount)}
        </Text.Normal>
      </span>
      {open && coords && (
        <TooltipPortal coordinates={{ x: coords.x, y: coords.y }} attachment="bottom center">
          <Box background="grey1000" borderRadius={{ all: 'abs008' }} padding="s16" width="s300">
            <Text.Normal color="grey000White" weight="bold" size="200">
              {title}
            </Text.Normal>
            {rows.map((row, idx) => (
              <FlexBetweenRow
                key={idx}
                style={{
                  paddingTop: idx === 0 ? '8px' : 0,
                  paddingBottom: '8px',
                }}
              >
                <Text.Normal color="grey000White" weight="semibold">
                  {row.left}
                </Text.Normal>
                {row.right !== undefined && (
                  <Text.Normal color="grey000White" weight="semibold">
                    {row.right}
                  </Text.Normal>
                )}
              </FlexBetweenRow>
            ))}
            {details && (
              <Text.Normal color="grey000White">
                {typeof details === 'string' ? details : details}
              </Text.Normal>
            )}
            {count === 0 && emptyMessage && (
              <Text.Normal color="grey000White">{emptyMessage}</Text.Normal>
            )}
          </Box>
        </TooltipPortal>
      )}
    </>
  )
}

export default HoverableTooltipRecommendation
